import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Typography,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PlayArrow as TestIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatConfigResponse, ChatProviderResponse, ChatProviderType } from '@/api/ApiClient';

interface ChatConfigEditDialogProps {
  open: boolean;
  config: Partial<ChatConfigResponse> | null;
  providers: ChatProviderResponse[];
  onClose: () => void;
  onSave: (config: Partial<ChatConfigResponse>) => void;
  onTest: (config: Partial<ChatConfigResponse>) => void;
}

export default function ChatConfigEditDialog({
  open,
  config,
  providers,
  onClose,
  onSave,
  onTest,
}: ChatConfigEditDialogProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<ChatConfigResponse>>({});

  useEffect(() => {
    if (config) {
      setFormData({ ...config });
    }
  }, [config]);

  const selectedProvider = providers.find(p => p.value === formData.provider_type);

  const handleProviderChange = (providerValue: string) => {
    const providerInfo = providers.find(p => p.value === providerValue);
    if (providerInfo) {
      setFormData({
        ...formData,
        provider_type: providerValue as ChatProviderType,
        model: providerInfo.default_models?.[0] || providerInfo.default_model || '',
        provider_config: {},
      });
    }
  };

  const handleProviderConfigChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      provider_config: {
        ...formData.provider_config,
        [key]: value,
      },
    });
  };

  const getFieldLabel = (providerType: string, fieldKey: string): string => {
    if (providerType === 'openai') {
      if (fieldKey === 'api_key') {return t('chatConfig.form.openaiApiKey');}
      if (fieldKey === 'organization') {return t('chatConfig.form.openaiOrganization');}
    } else if (providerType === 'ollama') {
      if (fieldKey === 'base_url') {return t('chatConfig.form.ollamaBaseUrl');}
    } else if (providerType === 'sap_aihub') {
      if (fieldKey === 'client_id') {return t('chatConfig.form.sapClientId');}
      if (fieldKey === 'client_secret') {return t('chatConfig.form.sapClientSecret');}
      if (fieldKey === 'service_url') {return t('chatConfig.form.sapServiceUrl');}
      if (fieldKey === 'resource_group') {return t('chatConfig.form.sapResourceGroup');}
    }
    return fieldKey;
  };

  const getFieldHelp = (providerType: string, fieldKey: string): string | undefined => {
    if (providerType === 'ollama' && fieldKey === 'base_url') {
      return t('chatConfig.form.ollamaUrlHelp');
    }
    return undefined;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {config?.id ? t('chatConfig.dialog.editTitle') : t('chatConfig.dialog.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label={t('chatConfig.form.displayName')}
              value={formData.display_name || ''}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('chatConfig.form.description')}
              multiline
              rows={2}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('chatConfig.form.provider')}</InputLabel>
              <Select
                value={formData.provider_type || ''}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('chatConfig.form.model')}
              value={formData.model || ''}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography gutterBottom>
              {t('chatConfig.form.temperature')}: {formData.temperature}
            </Typography>
            <Slider
              value={formData.temperature || 0.7}
              onChange={(_, value) => setFormData({ ...formData, temperature: value as number })}
              step={0.1}
              min={0}
              max={2}
              marks
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary">
              {t('chatConfig.form.temperatureHelp')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('chatConfig.form.maxTokens')}
              type="number"
              value={formData.max_tokens || ''}
              onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
              helperText={t('chatConfig.form.maxTokensHelp')}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('chatConfig.form.timeout')}
              type="number"
              value={formData.timeout || ''}
              onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.streaming || false}
                  onChange={(e) => setFormData({ ...formData, streaming: e.target.checked })}
                />
              }
              label={t('chatConfig.form.streaming')}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default || false}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label={t('chatConfig.form.isDefault')}
            />
          </Grid>

          {/* <Grid size={12}>
            <TextField
              fullWidth
              label={t('chatConfig.form.systemMessage')}
              multiline
              rows={4}
              value={formData.system_message || ''}
              onChange={(e) => setFormData({ ...formData, system_message: e.target.value })}
              helperText={t('chatConfig.form.systemMessageHelp')}
            />
          </Grid> */}

          {selectedProvider && (
            <>
              <Grid size={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('chatConfig.form.providerConfiguration')}
                  </Typography>
                </Divider>
              </Grid>

              {selectedProvider && Object.entries(selectedProvider.config_fields || {}).map(([configKey, fieldInfo]) => (
                <Grid size={{ xs: 12, md: 6 }} key={configKey}>
                  <TextField
                    fullWidth
                    label={getFieldLabel(formData.provider_type || '', configKey)}
                    type={fieldInfo.secret ? 'password' : 'text'}
                    value={formData.provider_config?.[configKey] || fieldInfo.default || ''}
                    onChange={(e) => handleProviderConfigChange(configKey, e.target.value)}
                    required={fieldInfo.required}
                    helperText={getFieldHelp(formData.provider_type || '', configKey)}
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('chatConfig.dialog.cancel')}</Button>
        <Button onClick={() => onTest(formData)} startIcon={<TestIcon />}>
          {t('chatConfig.dialog.test')}
        </Button>
        <Button onClick={() => onSave(formData)} variant="contained">
          {t('chatConfig.dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}